import { ChevronRight, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { getGlobalQuickActions } from '@/app/shell/navigationManifest';
import { closeFabMenu, toggleFabMenu } from '@/features/ui/store/uiSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { cn } from '@/utils/cn';

export function GlobalFab() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(state => state.ui.isFabMenuOpen);
  const permissions = useAppSelector(state => state.auth.permissions);
  const actions = getGlobalQuickActions(permissions);
  const location = useLocation();

  useEffect(() => {
    dispatch(closeFabMenu());
  }, [dispatch, location.pathname]);

  return (
    <div className="pointer-events-none fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-3 z-40 flex flex-col items-end sm:right-4 lg:hidden">
      <button
        aria-label="Close global actions"
        className={cn(
          'pointer-events-auto fixed inset-0 z-30 transition duration-200 ease-out',
          isOpen
            ? 'pointer-events-auto bg-slate-950/28 opacity-100 backdrop-blur-[2px] dark:bg-slate-950/56'
            : 'pointer-events-none bg-slate-950/0 opacity-0'
        )}
        onClick={() => dispatch(closeFabMenu())}
      />
      <div
        className={cn(
          'relative z-40 mb-3 w-[min(19rem,calc(100vw-1.5rem))] origin-bottom-right rounded-[1.9rem] border border-slate-200/80 bg-white/96 p-2.5 shadow-[0_28px_70px_-34px_rgba(15,23,42,0.38)] backdrop-blur transition-[opacity,transform] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-slate-800/90 dark:bg-slate-950/96 dark:shadow-[0_30px_80px_-38px_rgba(2,6,23,0.85)]',
          isOpen ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-4 scale-95 opacity-0'
        )}
      >
          <div className="px-2 pb-2 pt-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Quick Actions</p>
          </div>
          <div className="space-y-1.5">
          {actions.map((action, index) => (
            <Link
              key={action.label}
              to={action.to}
              onClick={() => dispatch(closeFabMenu())}
              className={cn(
                'tap-target flex min-h-[4.25rem] items-center gap-3 rounded-[1.4rem] border border-slate-200/80 bg-slate-50/80 px-3 py-3 text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] transition-[opacity,transform,background-color,border-color] duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] md:hover:border-slate-300 md:hover:bg-white dark:border-slate-800/80 dark:bg-slate-900/85 dark:text-slate-100 dark:md:hover:bg-slate-900',
                isOpen ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-[0.98] opacity-0'
              )}
              style={{ transitionDelay: isOpen ? `${index * 28}ms` : '0ms' }}
              aria-label={action.label}
            >
              <span
                className={cn(
                  'grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white shadow-[0_18px_30px_-20px_rgba(15,23,42,0.45)]',
                  action.className
                )}
              >
                <action.icon className="h-[18px] w-[18px]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{action.label}</span>
                <span className="block text-[11px] font-medium text-slate-500 dark:text-slate-400">Open action</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
            </Link>
          ))}
          </div>
      </div>

      <button
        aria-label="Global actions"
        className={cn(
          'tap-target pointer-events-auto relative z-40 grid h-[3.4rem] w-[3.4rem] place-items-center rounded-[1.3rem] border text-white shadow-[0_24px_40px_-24px_rgba(37,99,235,0.6)] transition-[transform,background-color,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.97]',
          isOpen
            ? 'rotate-45 border-slate-800 bg-slate-900 shadow-[0_24px_44px_-26px_rgba(15,23,42,0.7)] dark:border-slate-700 dark:bg-slate-100 dark:text-slate-900'
            : 'border-brand-500 bg-brand-600 md:hover:bg-brand-700'
        )}
        onClick={() => dispatch(toggleFabMenu())}
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}
